<template lang="pug" src="./establishments-list.pug" />
<style lang="scss" src="./establishments-list.scss" />

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import request from '@/utils/request';
import {VContainer} from "vuetify/lib/components";
import {localizeField, multilingualFieldKeys} from "@/helpers/locales";

export default {
    name: 'EstablishmentsList',
    components: {
      'v-container': VContainer
    },
    data: () => ({
        filters: {
            query: null,
            brandId: null,
            categoryId: null
        },
        requestProcessing: false,
        removeDialog: false,
        idToRemove: null,
        alarmStatus: false,

        settingsDialog: false,
        settings: {
            minutesPerKilometer: null,
        }
    }),
    computed: {
        ...mapGetters(['roleName', 'userBrandId']),
        ...mapState({
            items: state => state.establishments.items,
            pagination: state => state.establishments.pagination,
            categories: state => state.establishmentsCategories.allItems,
            brands: state => state.data.brands,
            types: state => state.establishmentsTypes.allItems
        }),
        headers() {
            const titles = [
                ...multilingualFieldKeys(['name', 'description']), 'status', 'cookTime',
                'minimalPrice', 'commission', 'margin', 'openTime', 'closeTime', 'closedOnAlarm', 'place', 'type', 'category',
                'brand', 'createdAt', 'updatedAt'
            ];

            if (this.filters.categoryId) {
                titles.unshift('position');
                titles.push('order');
            }

            // if (['admin', 'admin-delivery']) { //TODO: find out what it does
                titles.push('actions');
            // }
            return titles.map(it => ({
                text: this.$vuetify.lang.t(`$vuetify.pages.Establishments.table.${it}`),
                value: it,
                sortable: false,
                align: 'center'
            }));
        },
        isRemoved() {
            return this.filters.type === 'removed';
        },
        canAddEstablishment() {
            const availableRoles = ['admin', 'admin-delivery'];
            return availableRoles.includes(this.roleName);
        }
    },
    setup() {
        return {
            localizeField
        };
    },
    async created() {
        await this.loadBrands();
        await this.getTypes();
        await this.getCategories();
        await this.loadAlarm();

        if (this.userBrandId) {
            this.$set(this.filters, 'brandId', this.userBrandId);
        }
        if (this.canAddEstablishment) {
            this.loadSettings();
        }
        this.loadItems(this.pagination.page);
    },
    methods: {
        ...mapActions({
            loadBrands: 'data/getBrands',
            getItems: 'establishments/getItems',
            getTypes: 'establishmentsTypes/getAllItems',
            lowerEstablishmentRequest: 'establishments/lowerEstablishment',
            raiseEstablishmentRequest: 'establishments/raiseEstablishment',
            getCategories: 'establishmentsCategories/getAllItems'
        }),
        loadAlarm() {
          return request({ url: '/alarm', method: 'GET' })
              .then(res => {
                if (res.data && res.data.status) {
                  this.alarmStatus = res.data.status;
                }
              }).catch(Function.prototype);
        },
        setAlarm(newStatus) {
          return request({ url: `/alarm/${Number(newStatus)}`, method: 'PUT' })
              .then(res => {
                if (res.data && res.data.status) {
                  this.alarmStatus = res.data.status;
                }
              }).catch(Function.prototype);
        },
        onTypeChange(value) {
            const type = value ? 'removed' : 'active';
            this.$set(this.filters, 'type', type);

            this.loadItems();
        },
        loadItems(page = 1) {
            if (this.requestProcessing) return;

            this.requestProcessing = true;

            this.getItems(Object.assign({}, {
                page,
                ...this.filters
            })).then(() => {
                this.$nextTick(() => this.requestProcessing = false);
            });
        },
        lowerEstablishment(id) {
            if (this.requestProcessing) return;

            this.requestProcessing = true;

            this.lowerEstablishmentRequest(id).then(() => {
                this.$nextTick(() => {
                    this.requestProcessing = false;
                    this.loadItems(this.pagination.page);
                });
            });
        },
        raiseEstablishment(id) {
            if (this.requestProcessing) return;

            this.requestProcessing = true;

            this.raiseEstablishmentRequest(id).then(() => {
                this.$nextTick(() => {
                    this.requestProcessing = false;
                    this.loadItems(this.pagination.page);
                });
            });
        },
        loadSettings() {
            return request({
                method: 'GET',
                url: '/delivery/settings/minutesPerKilometer'
            }).then((res) => {
                if (res.data) {
                    this.settings = res.data;
                }
            }).catch(this.errorHandler);
        },
        onPageChange(page) {
            if (page !== this.pagination.page) {
                this.loadItems(page);
            }
        },
        openRemoveDialog(idToRemove) {
          this.removeDialog = true;
          this.idToRemove = idToRemove;
        },
        async remove() {
            if (this.requestProcessing) {
                return;
            }

            this.requestProcessing = true;
            return request({
                method: 'delete',
                url: `/delivery/establishment/${this.idToRemove}`
            }).then(res => {
                if (res.status === 200) {
                    this.idToRemove = null;
                    this.removeDialog = false;
                    this.requestProcessing = false;
                    this.loadItems();
                }
            }).catch(error => this.errorHandler(error));
        },
        restore(id) {
            return request({
                method: 'put',
                url: `/delivery/establishment-restore/${id}`
            }).then(res => {
                if (res.status === 200) {
                    this.loadItems();
                }
            }).catch(error => this.errorHandler(error));
        },
        errorHandler(error) {
            const message = error.response && error.response.data && error.response.data.error;

            if (message) {
                this.$store.dispatch('alert/addMessage', {
                    message: `establishments.${message}`
                });
            }
        },
        openSettingsDialog() {
            this.settingsDialog = true;
        },
        saveSettings() {
            return request({
                method: 'POST',
                url: '/delivery/settings/minutesPerKilometer',
                data: this.settings,
            }).then(res => {
                if (res.data) {
                    this.$store.dispatch('alert/addMessage', {
                        type: 'success',
                        message: 'success'
                    });
                    this.settings.minutesPerKilometer = res.data.value;
                    this.closeSettingsDialog();
                }
            }).catch(error => this.errorHandler(error));
        },
        closeSettingsDialog() {
            this.settingsDialog = false;
        }
    }
};
</script>
